import type {
	InboundAnsweringPolicy,
	TransferToHumanDestination,
} from "./types";

export const uniqueId = (prefix = "") => {
	return (
		prefix + Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
	);
};

export const shortUuid = (prefix = "") => {
	const cryptoLike = globalThis as typeof globalThis & {
		crypto?: { randomUUID?: () => string };
	};

	if (typeof cryptoLike.crypto?.randomUUID === "function") {
		return (
			prefix + cryptoLike.crypto.randomUUID().replace(/-/g, "").slice(0, 12)
		);
	}

	return uniqueId(prefix);
};

export const getErrorMessage = (error: unknown) => {
	return error instanceof Error
		? error.message
		: typeof error === "string"
			? error
			: "Unknown error";
};

export const normalized = (value?: string) =>
	value ? value.toLowerCase() : "";

const INBOUND_ANSWERING_POLICIES = new Set<InboundAnsweringPolicy>([
	"AI_FIRST",
	"AI_ONLY",
	"HUMAN_FIRST",
	"HUMAN_ONLY",
]);

export const getAgentInboundAnsweringPolicy = (
	agent?: {
		inboundAnsweringPolicy?: unknown;
		status?: unknown;
	} | null,
): InboundAnsweringPolicy => {
	const explicitPolicy = agent?.inboundAnsweringPolicy;
	if (
		typeof explicitPolicy === "string" &&
		INBOUND_ANSWERING_POLICIES.has(explicitPolicy as InboundAnsweringPolicy)
	) {
		return explicitPolicy as InboundAnsweringPolicy;
	}

	return agent?.status === "Paused" ||
		agent?.status === "paused" ||
		agent?.status === "Disabled" ||
		agent?.status === "disabled"
		? "HUMAN_ONLY"
		: "AI_FIRST";
};

export const isSipUri = (value: unknown): boolean => {
	if (typeof value !== "string") return false;
	const trimmed = value.trim().toLowerCase();
	if (!trimmed) return false;
	return (
		trimmed.startsWith("sip:") ||
		trimmed.startsWith("sips:") ||
		(trimmed.includes("@") && !trimmed.includes(" "))
	);
};

export const formatSipUri = (value: string): string => {
	const trimmed = value.trim();
	if (
		trimmed.toLowerCase().startsWith("sip:") ||
		trimmed.toLowerCase().startsWith("sips:")
	) {
		return trimmed;
	}
	return `sip:${trimmed}`;
};

export const getAgentTransferDestinations = (
	agent?: {
		transferToHumanDestinations?: TransferToHumanDestination[] | null;
		transferToHumanPhoneNumbers?: string[] | null;
		transferToHumanPhoneNumber?: string | null;
	} | null,
): TransferToHumanDestination[] => {
	if (!agent) return [];
	if (
		Array.isArray(agent.transferToHumanDestinations) &&
		agent.transferToHumanDestinations.length > 0
	) {
		return agent.transferToHumanDestinations
			.filter(
				(destination): destination is TransferToHumanDestination =>
					destination !== null &&
					typeof destination === "object" &&
					typeof destination.id === "string" &&
					Boolean(destination.id.trim()) &&
					typeof destination.address === "string" &&
					Boolean(destination.address.trim()),
			)
			.map((destination) => ({
				...destination,
				id: destination.id.trim(),
				address: destination.address.trim(),
				type: destination.type === "sip" ? "sip" : "pstn",
				priority:
					Number.isInteger(destination.priority) && destination.priority > 0
						? destination.priority
						: 1,
				availability:
					destination.availability?.mode === "custom"
						? {
								mode: "custom" as const,
								windows: Array.isArray(destination.availability.windows)
									? destination.availability.windows
									: [],
							}
						: { mode: "always" as const },
			}));
	}

	const legacyTargets: string[] = [];
	if (
		Array.isArray(agent.transferToHumanPhoneNumbers) &&
		agent.transferToHumanPhoneNumbers.length > 0
	) {
		legacyTargets.push(
			...agent.transferToHumanPhoneNumbers
				.filter(
					(entry): entry is string =>
						typeof entry === "string" && Boolean(entry.trim()),
				)
				.map((entry) => entry.trim()),
		);
	} else if (
		typeof agent.transferToHumanPhoneNumber === "string" &&
		agent.transferToHumanPhoneNumber.trim()
	) {
		legacyTargets.push(agent.transferToHumanPhoneNumber.trim());
	}
	return legacyTargets.map((address, index) => ({
		id: `legacy-${index + 1}`,
		type: isSipUri(address) ? "sip" : "pstn",
		address,
		priority: 1,
		availability: { mode: "always" },
	}));
};

export const getAgentTransferTargets = (
	agent?: Parameters<typeof getAgentTransferDestinations>[0],
): string[] => {
	return getAgentTransferDestinations(agent).map(
		(destination) => destination.address,
	);
};
